import type {
  FinalizedPaymentContract,
  IssuanceSnapshotContract,
  ObligationContract,
  ObligationFamilyContract,
  PaymentMethodContract,
} from "./financial.js";

export type ObligationActionKindContract = "discount" | "waiver" | "adjustment";

export interface ObligationActionInputContract {
  readonly kind: ObligationActionKindContract;
  readonly amountMad?: string;
  readonly effectiveDate: string;
  readonly reason: string;
}

export interface ObligationActionResultContract {
  readonly obligation: ObligationContract;
  readonly action: {
    readonly id: string;
    readonly kind: ObligationActionKindContract;
    readonly amountMad: string;
    readonly effectiveDate: string;
    readonly reason: string;
    readonly actorUserId: string;
    readonly serverCreatedAt: string;
  };
}

export interface StudentServiceAmountsInputContract {
  readonly amountsMad: Partial<Record<ObligationFamilyContract, string>>;
  readonly calendarSnapshotId: string;
  readonly effectiveDate: string;
  readonly reason: string;
}

export interface StudentServiceAmountsResultContract {
  readonly obligations: readonly ObligationContract[];
  readonly changedCount: number;
}

export type SchoolCycleContract = "preschool" | "primary";

export interface SchoolClassSettingContract {
  readonly label: string;
  readonly cycle: SchoolCycleContract;
  readonly nextClassLabel: string;
  /** Optional tariff group when a level shares a cycle but uses the other tuition tariff. */
  readonly tuitionTariff?: SchoolCycleContract;
}

export const SCHOOL_LEVELS = [
  { label: "PS", cycle: "preschool", nextClassLabel: "MS" },
  { label: "MS", cycle: "preschool", nextClassLabel: "GS" },
  { label: "GS", cycle: "preschool", nextClassLabel: "CP" },
  { label: "CP", cycle: "primary", nextClassLabel: "CE1" },
  { label: "CE1", cycle: "primary", nextClassLabel: "CE2" },
  { label: "CE2", cycle: "primary", nextClassLabel: "CM1" },
  { label: "CM1", cycle: "primary", nextClassLabel: "CM2" },
  { label: "CM2", cycle: "primary", nextClassLabel: "6ème" },
  { label: "6ème", cycle: "primary", nextClassLabel: "6ème" },
] as const satisfies readonly SchoolClassSettingContract[];

export const ALHODA_LEVELS = [
  { label: "TPS", cycle: "preschool", nextClassLabel: "PS", tuitionTariff: "primary" },
  { label: "PS", cycle: "preschool", nextClassLabel: "MS" },
  { label: "MS", cycle: "preschool", nextClassLabel: "GS" },
  { label: "GS", cycle: "preschool", nextClassLabel: "GS" },
] as const satisfies readonly SchoolClassSettingContract[];

export const SUPPORTED_SCHOOL_LEVELS = [...SCHOOL_LEVELS, ...ALHODA_LEVELS] as const;

export function normalizeSchoolLevelLabel(value: string): string {
  const trimmed = value.trim();
  const normalized = trimmed.replace(/-[AB]$/i, "");
  return SUPPORTED_SCHOOL_LEVELS.some((entry) => entry.label === normalized) ? normalized : trimmed;
}

export function isSchoolLevelLabel(value: string): boolean {
  return SUPPORTED_SCHOOL_LEVELS.some((entry) => entry.label === value.trim());
}

export function isChamsSchoolProfile(classes: readonly SchoolClassSettingContract[]): boolean {
  return (
    classes.length === SCHOOL_LEVELS.length &&
    classes.every((entry, index) => {
      const expected = SCHOOL_LEVELS[index];
      return (
        expected !== undefined &&
        entry.label === expected.label &&
        entry.cycle === expected.cycle &&
        entry.nextClassLabel === expected.nextClassLabel
      );
    })
  );
}

export function sectionRequiredForLevel(
  classes: readonly SchoolClassSettingContract[],
  classLabel: string,
): boolean {
  return (
    isChamsSchoolProfile(classes) &&
    classLabel !== "PS" &&
    SCHOOL_LEVELS.some((entry) => entry.label === classLabel)
  );
}

export function sectionLabelValidForEnrollment(
  classes: readonly SchoolClassSettingContract[],
  classLabel: string,
  sectionLabel: "A" | "B" | null,
): boolean {
  if (!isChamsSchoolProfile(classes)) return sectionLabel === null;
  return classLabel === "PS"
    ? sectionLabel === null
    : SCHOOL_LEVELS.some((entry) => entry.label === classLabel) &&
        (sectionLabel === "A" || sectionLabel === "B");
}

export function sectionLabelAfterPromotion(
  classLabel: string,
  nextClassLabel: string,
  sectionLabel: "A" | "B" | null,
): "A" | "B" | null {
  return classLabel === "PS" && nextClassLabel === "MS" ? null : sectionLabel;
}

export interface SchoolSettingsContract {
  readonly schoolNameAr: string;
  readonly schoolNameFr: string;
  readonly email: string;
  readonly phone: string;
  readonly addressAr: string;
  readonly addressFr: string;
  readonly academicYear: string;
  readonly activeFrom: string;
  readonly activeThrough: string;
  readonly amountsMad: {
    readonly preschoolRegistration: string;
    readonly primaryRegistration: string;
    readonly preschoolTuition: string;
    readonly primaryTuition: string;
    readonly transport: string;
    readonly accompaniment: string;
  };
  readonly classes: readonly SchoolClassSettingContract[];
  readonly advanceYearAvailable: boolean;
  readonly advanceYear: {
    readonly academicYear: string;
    readonly activeFrom: string;
    readonly activeThrough: string;
    readonly calendarSnapshotId: string;
    readonly amountsMad: SchoolSettingsContract["amountsMad"];
    readonly eligibleStudents: number;
    readonly obligationsCreated: number;
    readonly activatedAt: string;
  } | null;
  readonly advanceYearEligibleStudents: number;
  readonly updatedAt: string;
}

export interface SchoolSettingsInputContract {
  readonly schoolNameAr: string;
  readonly schoolNameFr: string;
  readonly email: string;
  readonly phone: string;
  readonly addressAr: string;
  readonly addressFr: string;
  readonly amountsMad: SchoolSettingsContract["amountsMad"];
  readonly classes: readonly SchoolClassSettingContract[];
}

export interface SchoolRolloverInputContract {
  readonly academicYear: string;
}

export interface SchoolAdvanceYearInputContract {
  readonly academicYear: string;
  readonly amountsMad: SchoolSettingsContract["amountsMad"];
}

export interface SchoolAdvanceYearResultContract {
  readonly settings: SchoolSettingsContract;
  readonly eligibleStudents: number;
  readonly obligationsCreated: number;
}

export interface SchoolRolloverResultContract {
  readonly settings: SchoolSettingsContract;
  readonly studentsAdvanced: number;
  readonly obligationsCreated: number;
}

export type PaymentLifecycleContract = "pending" | "settled" | "rejected" | "cancelled";

export interface OperationalPaymentContract {
  readonly payment: FinalizedPaymentContract;
  readonly document: IssuanceSnapshotContract;
  readonly originalDocument?: IssuanceSnapshotContract;
  readonly lifecycle: PaymentLifecycleContract;
  readonly synchronized: true;
  readonly settlementDate?: string;
  readonly cancellation?: {
    readonly date: string;
    readonly reason: string;
    readonly actorNameAr: string;
    readonly actorNameFr: string;
    readonly serverCreatedAt: string;
  };
  readonly operator: {
    readonly nameAr: string;
    readonly nameFr: string;
  };
  readonly canCancel: boolean;
  readonly guardianShareEligible: boolean;
}

export interface InstrumentQueueItemContract {
  readonly paymentId: string;
  readonly reference: string;
  readonly student: {
    readonly id: string;
    readonly internalReference: string;
    readonly nameAr: string;
    readonly nameFr: string;
  };
  readonly amountMad: string;
  readonly receivedDate: string;
  readonly method: Exclude<PaymentMethodContract, { readonly method: "cash" }>;
  readonly status: "pending" | "cleared" | "verified" | "rejected";
  readonly bankStatementDate?: string;
  readonly reason?: string;
  readonly acknowledgement: IssuanceSnapshotContract;
}

export interface InstrumentTransitionInputContract {
  readonly action: "settle" | "reject";
  readonly bankStatementDate: string;
  readonly bankStatementReference?: string;
  readonly reason?: string;
}

export interface DashboardContract {
  readonly period: {
    readonly from: string;
    readonly to: string;
  };
  readonly generatedAt: string;
  readonly currentDate: string;
  readonly currentAcademicYear: string;
  readonly collectionScope: {
    readonly kind: "month" | "academic-year";
    readonly academicYear: string;
    readonly from: string;
    readonly to: string;
    readonly month?: string;
  };
  readonly collection: {
    readonly netObligationsMad: string;
    readonly settledAllocationsMad: string;
    readonly percentage: string | null;
  };
  readonly income: {
    readonly grossMad: string;
    readonly cancellationsMad: string;
    readonly netMad: string;
  };
  readonly activity: {
    readonly granularity: "day" | "month";
    readonly points: readonly {
      readonly period: string;
      readonly cashMad: string;
      readonly chequeMad: string;
      readonly transferMad: string;
      readonly netMad: string;
    }[];
  };
  readonly overdue: {
    readonly students: number;
    readonly amountMad: string;
  };
  readonly arrears: {
    readonly oneMonthStudents: number;
    readonly oneMonthMad: string;
    readonly twoMonthStudents: number;
    readonly twoMonthMad: string;
    readonly threePlusMonthStudents: number;
    readonly threePlusMonthMad: string;
    readonly urgentStudents: number;
    readonly urgentMad: string;
  };
  readonly overdueRegistration: {
    readonly students: number;
    readonly amountMad: string;
  };
  readonly pending: {
    readonly chequeCount: number;
    readonly chequeMad: string;
    readonly transferCount: number;
    readonly transferMad: string;
    readonly oldestReceivedDate?: string;
  };
  readonly academicCollection: {
    readonly academicYear: string;
    readonly availableAcademicYears: readonly string[];
    readonly points: readonly {
      readonly month: string;
      readonly netObligationsMad: string;
      readonly settledAllocationsMad: string;
      readonly percentage: string | null;
    }[];
  };
}

export interface ReportBreakdownContract {
  readonly key: string;
  readonly labelAr: string;
  readonly labelFr: string;
  readonly grossMad: string;
  readonly cancellationsMad: string;
  readonly netMad: string;
}

export interface ReportActivityContract {
  readonly kind: "settlement" | "cancellation";
  readonly date: string;
  readonly reference: string;
  readonly method: PaymentMethodContract["method"];
  readonly amountMad: string;
  readonly studentNameAr: string;
  readonly studentNameFr: string;
  readonly paidPeriods: readonly string[];
  readonly operatorNameAr: string;
  readonly operatorNameFr: string;
}

export interface ReportDailyTotalContract {
  readonly date: string;
  readonly cashMad: string;
  readonly chequeMad: string;
  readonly transferMad: string;
  readonly totalMad: string;
}

export interface OperationalReportContract {
  readonly mode: "daily" | "monthly";
  readonly period: {
    readonly from: string;
    readonly to: string;
  };
  readonly generatedAt: string;
  readonly totals: {
    readonly grossMad: string;
    readonly cancellationsMad: string;
    readonly netMad: string;
  };
  readonly byMethod: readonly ReportBreakdownContract[];
  readonly byOperator: readonly ReportBreakdownContract[];
  readonly activity: readonly ReportActivityContract[];
  readonly dailyTotals: readonly ReportDailyTotalContract[];
  readonly pendingInstruments: {
    readonly chequeCount: number;
    readonly transferCount: number;
  };
  readonly arrears: DashboardContract["arrears"];
  readonly overdueRegistration: DashboardContract["overdueRegistration"];
}

export interface ReceiptDocumentContract {
  readonly paymentId: string;
  readonly snapshot: IssuanceSnapshotContract;
  readonly lifecycle: "settled" | "cancelled";
  readonly settlementDate: string;
  readonly cancellation?: {
    readonly date: string;
    readonly reason: string;
  };
}

export interface ShareableFinancialDocumentContract {
  readonly paymentId: string;
  readonly snapshot: IssuanceSnapshotContract;
  readonly lifecycle: "pending" | "settled" | "cancelled";
  readonly settlementDate?: string;
  readonly cancellation?: ReceiptDocumentContract["cancellation"];
}

export interface ReceiptCapabilityContract {
  readonly active: boolean;
  readonly version?: number;
  readonly url?: string;
  readonly whatsappEligible: boolean;
}

export interface WhatsAppIntentContract {
  readonly intentUrl: string;
  readonly launched: false;
  readonly requiresOperatorConfirmation: true;
}
